import type { ApiEnvelope } from '../types';
import type { ApiModuleDeps } from './internal/request';
import type {
    ApiInvite,
    ApiProject,
    ApiSkillContent,
    ApiSkillDetail,
    ApiWorkspace,
    ApiWorkspaceMember,
    ApiWorkspaceSkillsResponse,
    SupabaseOrgInfo,
    WorkspaceExperimentFlags,
} from '../types';

export function createWorkspacesApi({ authHttp }: ApiModuleDeps) {
    return {
        async getWorkspaceExperimentFlags(
            workspace_id: number | string,
            options?: { signal?: AbortSignal }
        ): Promise<ApiEnvelope<{ flags: WorkspaceExperimentFlags }>> {
            return authHttp.get<ApiEnvelope<{ flags: WorkspaceExperimentFlags }>>(
                `/workspaces/${encodeURIComponent(String(workspace_id))}/experiment-flags`,
                { signal: options?.signal }
            );
        },

        async listWorkspaceSkills(
            workspace_id: number | string
        ): Promise<ApiEnvelope<ApiWorkspaceSkillsResponse>> {
            const response = await authHttp.get<ApiEnvelope<ApiWorkspaceSkillsResponse>>(
                `/workspaces/${workspace_id}/skills`
            );
            return response;
        },

        async getWorkspaceSkillDetail(
            workspace_id: number | string,
            skill_key: string,
            version?: number
        ): Promise<ApiEnvelope<ApiSkillDetail>> {
            const response = await authHttp.get<ApiEnvelope<ApiSkillDetail>>(
                `/workspaces/${workspace_id}/skills/${encodeURIComponent(skill_key)}`,
                {
                    query: version ? { version } : undefined,
                }
            );
            return response;
        },

        async getWorkspaceSkillContent(
            workspace_id: number | string,
            params: {
                project_id?: string;
                path?: string;
                skill_key?: string;
                version?: number;
            }
        ): Promise<ApiEnvelope<ApiSkillContent>> {
            const response = await authHttp.get<ApiEnvelope<ApiSkillContent>>(
                `/workspaces/${workspace_id}/skills/content`,
                {
                    query: params,
                }
            );
            return response;
        },

        async renameWorkspaceSkill(
            workspace_id: number,
            skill_key: string,
            request: { name: string }
        ): Promise<ApiEnvelope<ApiSkillDetail>> {
            const response = await authHttp.patch<ApiEnvelope<ApiSkillDetail>>(
                `/workspaces/${workspace_id}/skills/${encodeURIComponent(skill_key)}`,
                request
            );
            return response;
        },

        async deleteWorkspaceSkill(
            workspace_id: number,
            skill_key: string
        ): Promise<ApiEnvelope<null>> {
            const response = await authHttp.delete<ApiEnvelope<null>>(
                `/workspaces/${workspace_id}/skills/${encodeURIComponent(skill_key)}`
            );
            return response;
        },

        async checkProjectWorkspace(project_id: string): Promise<ApiEnvelope<null>> {
            const response = await authHttp.post<ApiEnvelope<null>>(
                `/projects/${project_id}/workspace/check`
            );
            return response;
        },

        async resetProjectWorkspaces(project_id: string): Promise<ApiEnvelope<null>> {
            const response = await authHttp.post<ApiEnvelope<null>>(
                `/projects/${project_id}/workspace/reset`
            );
            return response;
        },

        async listWorkspaces(options?: {
            signal?: AbortSignal;
        }): Promise<ApiEnvelope<{ workspaces: ApiWorkspace[] }>> {
            const response = await authHttp.get<ApiEnvelope<{ workspaces: ApiWorkspace[] }>>(
                '/workspaces',
                { signal: options?.signal }
            );
            return response;
        },

        /** 按数字 ID 或公开 UUID 读取详情；响应不包含成员角色，权限由后端校验。 */
        async getWorkspace(
            workspace_id: number | string,
            options?: { signal?: AbortSignal }
        ): Promise<ApiEnvelope<{ workspace: Omit<ApiWorkspace, 'role'> }>> {
            return authHttp.get<ApiEnvelope<{ workspace: Omit<ApiWorkspace, 'role'> }>>(
                `/workspaces/${encodeURIComponent(String(workspace_id))}`,
                { signal: options?.signal }
            );
        },

        async createWorkspace(
            name: string,
            image: string = ''
        ): Promise<ApiEnvelope<{ workspace: ApiWorkspace }>> {
            const response = await authHttp.post<ApiEnvelope<{ workspace: ApiWorkspace }>>(
                '/workspaces',
                {
                    name,
                    image,
                }
            );
            return response;
        },

        async getWorkspaceMembers(
            workspace_id: number
        ): Promise<ApiEnvelope<{ members: ApiWorkspaceMember[] }>> {
            const response = await authHttp.get<ApiEnvelope<{ members: ApiWorkspaceMember[] }>>(
                `/workspaces/${workspace_id}/members`
            );
            return response;
        },

        async updateWorkspace(
            workspace_id: number,
            data: { name?: string; image?: string }
        ): Promise<ApiEnvelope<{ workspace: ApiWorkspace }>> {
            const response = await authHttp.post<ApiEnvelope<{ workspace: ApiWorkspace }>>(
                `/workspaces/${workspace_id}`,
                data
            );
            return response;
        },

        async leaveWorkspace(workspace_id: number): Promise<ApiEnvelope<null>> {
            const response = await authHttp.post<ApiEnvelope<null>>(
                `/workspaces/${workspace_id}/leave`
            );
            return response;
        },

        async inviteWorkspaceMember(
            workspace_id: number,
            data: { email: string; role: 'admin' | 'editor' | 'viewer' }
        ): Promise<ApiEnvelope<{ invite: ApiInvite }>> {
            const response = await authHttp.post<ApiEnvelope<{ invite: ApiInvite }>>(
                `/workspaces/${workspace_id}/members/invite`,
                data
            );
            return response;
        },

        async updateWorkspaceMemberRole(
            workspace_id: number,
            data: { email: string; role: 'admin' | 'editor' | 'viewer' }
        ): Promise<ApiEnvelope<{ member: ApiWorkspaceMember }>> {
            const response = await authHttp.post<ApiEnvelope<{ member: ApiWorkspaceMember }>>(
                `/workspaces/${workspace_id}/members/update-role`,
                data
            );
            return response;
        },

        async removeWorkspaceMember(
            workspace_id: number,
            data: { email: string }
        ): Promise<ApiEnvelope<null>> {
            const response = await authHttp.post<ApiEnvelope<null>>(
                `/workspaces/${workspace_id}/members/remove`,
                data
            );
            return response;
        },

        async listSupabaseOrgs(
            workspace_id: number
        ): Promise<ApiEnvelope<{ info: SupabaseOrgInfo[] }>> {
            const response = await authHttp.get<ApiEnvelope<{ info: SupabaseOrgInfo[] }>>(
                `/workspaces/${workspace_id}/integration/supabase/orgs`
            );
            return response;
        },

        async addSupabaseOrg(
            workspace_id: number,
            authorization_code: string,
            redirect_uri: string
        ): Promise<ApiEnvelope<{ info: SupabaseOrgInfo }>> {
            const response = await authHttp.post<ApiEnvelope<{ info: SupabaseOrgInfo }>>(
                `/workspaces/${workspace_id}/integration/supabase/add-org`,
                null,
                {
                    query: {
                        authorization_code,
                        redirect_uri: redirect_uri?.split('?')[0],
                    },
                }
            );
            return response;
        },

        async removeSupabaseOrg(workspace_id: number, org_id: string): Promise<ApiEnvelope<null>> {
            const response = await authHttp.delete<ApiEnvelope<null>>(
                `/workspaces/${workspace_id}/integration/supabase/remove-org`,
                {
                    query: {
                        org_id,
                    },
                }
            );
            return response;
        },

        async connectProjectSupabase(
            project_id: string,
            supa_project_id: string,
            chatId?: string
        ): Promise<ApiEnvelope<{ project: ApiProject }>> {
            const response = await authHttp.post<ApiEnvelope<{ project: ApiProject }>>(
                `/projects/${project_id}/connect-supabase`,
                null,
                {
                    query: {
                        supa_project_id,
                        ...(chatId ? { chat_id: chatId } : {}),
                    },
                }
            );
            return response;
        },

        async disconnectProjectSupabase(project_id: string): Promise<ApiEnvelope<null>> {
            const response = await authHttp.delete<ApiEnvelope<null>>(
                `/projects/${project_id}/disconnect-supabase`
            );
            return response;
        },
    };
}
